import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useInRouterContext, useSearchParams } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { Avatar } from "@/catalyst/avatar.tsx";
import { SidebarLabel } from "@/catalyst/sidebar.tsx";
import { setSelectedWorkspace } from "@/features/Workspace/Workspace.slice.ts";
import { Workspace } from "@/types/generated/management-api-types";

// Custom hook that safely handles useSearchParams when not in router context
function useSafeSearchParams(): {
    searchParams: URLSearchParams | null;
    setSearchParams: ((params: URLSearchParams) => void) | null;
} {
    const isInRouterContext = useInRouterContext();

    // Always try to use the real hook, but catch the error if not in router context
    let searchParams: URLSearchParams | null = null;
    let setSearchParams: ((params: URLSearchParams) => void) | null = null;

    try {
        // This will throw if not in router context
        const [realSearchParams, realSetSearchParams] = useSearchParams();
        if (isInRouterContext) {
            searchParams = realSearchParams;
            setSearchParams = realSetSearchParams;
        }
    } catch {
        // Use dummy values when not in router context
    }

    return {
        searchParams,
        setSearchParams,
    };
}

/** Makes a workspace the selected one, for either picker. */
export function useSwitchWorkspace(): (workspace: Workspace) => void {
    const dispatch = useDispatch();
    const { searchParams, setSearchParams } = useSafeSearchParams();

    return useCallback(
        (workspace: Workspace) => {
            // Clear the "q" query parameter before switching workspaces
            if (searchParams && setSearchParams && searchParams.has("q")) {
                searchParams.delete("q");
                setSearchParams(searchParams);
            }
            dispatch(setSelectedWorkspace(workspace));
        },
        [dispatch, searchParams, setSearchParams],
    );
}

export function WorkspaceAvatar({
    workspace,
    className,
    slot,
}: {
    workspace: Workspace;
    className?: string;
    slot?: string;
}) {
    return (
        <Avatar
            slot={slot}
            src={workspace.favicon}
            initials={workspace.name.slice(0, 2)}
            className={className ?? "bg-near-white"}
            textClassName={"text-near-black"}
        />
    );
}

/** What the sidebar item that opens a picker shows: the workspace in use. */
export function PickerTriggerContent({ selected }: { selected: Workspace }) {
    return (
        <>
            <WorkspaceAvatar workspace={selected} />
            <SidebarLabel>{selected.name}</SidebarLabel>
            <ChevronDownIcon />
        </>
    );
}
